/**
 * Users Admin View
 *
 * Manage users across projects: view user details, channel links,
 * project assignments, and message history.
 */

import { html, nothing } from "lit";

export type UserChannel = {
  channel: string;
  channelUserId: string;
};

export type UserEntry = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  username: string | null;
  position: string | null;
  profileDescription: string | null;
  channels: UserChannel[];
  preferredChannel: string | null;
  timezone: string;
  metadata?: Record<string, unknown>;
  projectCount?: number;
  messageCount?: number;
  lastSeenAt?: string | Date;
  createdAt: string | Date;
};

export type UserProjectAssignment = {
  projectId: string;
  projectName: string;
  role: "admin" | "project_admin" | "user";
  isActive: boolean;
};

export type UsersAdminProps = {
  loading: boolean;
  error: string | null;
  users: UserEntry[];
  searchQuery: string;
  selectedUser: UserEntry | null;
  selectedUserProjects: UserProjectAssignment[];
  editMode: boolean;
  editForm: Partial<UserEntry> | null;
  createMode: boolean;
  createForm: Partial<UserEntry> | null;
  projects: { id: string; name: string }[];
  onRefresh: () => void;
  onSearchChange: (query: string) => void;
  onSelectUser: (id: string) => void;
  onCreateUser: () => void;
  onSaveNewUser: () => void;
  onCancelCreate: () => void;
  onCreateFormChange: (field: string, value: unknown) => void;
  onAddToProject: (
    userId: string,
    projectId: string,
    role: "admin" | "project_admin" | "user",
  ) => void;
  onChangeRole: (
    userId: string,
    projectId: string,
    role: "admin" | "project_admin" | "user",
  ) => void;
  onRemoveFromProject: (userId: string, projectId: string) => void;
  onDeleteUser: (id: string) => void;
  onEditUser: () => void;
  onCancelEdit: () => void;
  onSaveUser: () => void;
  onEditFormChange: (field: string, value: unknown) => void;
  onResetPassword: (id: string) => void;
  // Email credentials
  emailCreds: EmailCredEntry | null;
  emailCredsLoading: boolean;
  emailCredsSaving: boolean;
  emailCredsError: string | null;
  emailCredsTestResult: string | null;
  emailCredsForm: Partial<EmailCredEntry> | null;
  onEmailCredsFormChange: (field: string, value: unknown) => void;
  onEmailCredsSave: (userId: string) => void;
  onEmailCredsDelete: (userId: string) => void;
  onEmailCredsTest: (userId: string) => void;
  onEmailCredsGmailPreset: () => void;
};

export type EmailCredEntry = {
  id?: string;
  userId?: string;
  type: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  username: string;
  fromAddress: string;
  fromName: string;
  tlsReject: boolean;
  isActive: boolean;
  hasPassword?: boolean;
};

/** Build a display name from firstName + lastName. */
function userDisplayName(user: { firstName?: string | null; lastName?: string | null }): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || "Unknown";
}

const CHANNEL_OPTIONS = [
  "telegram",
  "whatsapp",
  "email",
  "webchat",
  "discord",
  "slack",
  "signal",
  "avatar",
] as const;

export function renderUsersAdmin(props: UsersAdminProps) {
  const filtered = props.searchQuery
    ? props.users.filter((u) => {
        const q = props.searchQuery.toLowerCase();
        return (
          u.firstName?.toLowerCase().includes(q) ||
          u.lastName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.username?.toLowerCase().includes(q) ||
          u.position?.toLowerCase().includes(q) ||
          (u.channels ?? []).some((c) => c.channelUserId.toLowerCase().includes(q)) ||
          u.id.includes(q)
        );
      })
    : props.users;

  return html`
    <section class="users-layout">
      <!-- Users List -->
      <div class="card">
        <div class="card-title">
          Users
          <span class="muted">(${props.users.length})</span>
        </div>

        <div style="display:flex;gap:8px;margin-bottom:12px;">
          <input
            type="text"
            placeholder="Search users..."
            .value=${props.searchQuery}
            @input=${(e: Event) => props.onSearchChange((e.target as HTMLInputElement).value)}
            style="flex:1;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
          />
          <button class="btn btn--sm" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <button class="btn btn--sm primary" @click=${props.onCreateUser}>
            + New User
          </button>
        </div>

        ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

        <div style="max-height:600px;overflow-y:auto;">
          ${
            filtered.length === 0
              ? html`
                  <div class="muted" style="padding: 16px">No users found.</div>
                `
              : filtered.map(
                  (user) => html`
                  <div
                    style="padding:10px 12px;border:1px solid var(--border);border-radius:6px;margin-bottom:6px;cursor:pointer;
                           ${props.selectedUser?.id === user.id ? "border-color:var(--accent);" : ""}"
                    @click=${() => props.onSelectUser(user.id)}
                  >
                    <div style="display:flex;justify-content:space-between;">
                      <strong>${userDisplayName(user)}</strong>
                      ${user.username ? html`<span class="muted" style="font-size:0.85em;margin-left:6px;">@${user.username}</span>` : nothing}
                      <span class="muted" style="font-size:0.8em;">
                        ${user.projectCount ?? 0} projects
                      </span>
                    </div>
                    <div class="muted" style="font-size:0.8em;margin-top:2px;">
                      ${renderChannelBadges(user)}
                    </div>
                  </div>
                `,
                )
          }
        </div>
      </div>

      <!-- User Detail -->
      <div class="card">
        ${
          props.createMode
            ? renderCreateUserForm(props)
            : props.selectedUser
              ? renderUserDetail(props)
              : html`
                  <div class="muted" style="padding: 24px; text-align: center">
                    Select a user to view details, or create a new one.
                  </div>
                `
        }
      </div>
    </section>
  `;
}

function renderUserDetail(props: UsersAdminProps) {
  const user = props.selectedUser!;

  if (props.editMode && props.editForm) {
    return renderUserEditForm(props);
  }

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <div class="card-title">${userDisplayName(user)}</div>
      <div style="display:flex;gap:6px;">
        <button class="btn btn--sm" @click=${props.onEditUser}>Edit</button>
        <button class="btn btn--sm" style="color:var(--danger,#e55);" @click=${() => props.onDeleteUser(user.id)}>Delete</button>
      </div>
    </div>

    <div class="detail-stat-grid" style="margin:12px 0;">
      <div class="stat">
        <div class="stat-value">${user.projectCount ?? 0}</div>
        <div class="stat-label">Projects</div>
      </div>
      <div class="stat">
        <div class="stat-value">${user.messageCount ?? 0}</div>
        <div class="stat-label">Messages</div>
      </div>
      <div class="stat">
        <div class="stat-value">${user.timezone}</div>
        <div class="stat-label">Timezone</div>
      </div>
    </div>

    ${
      user.position
        ? html`
      <div style="margin-bottom:12px;">
        <strong>Position:</strong>
        <span style="margin-left:6px;">${user.position}</span>
      </div>
    `
        : nothing
    }

    ${
      user.profileDescription
        ? html`
      <div style="margin-bottom:12px;">
        <strong>Profile:</strong>
        <div class="muted" style="margin-top:4px;white-space:pre-wrap;">${user.profileDescription}</div>
      </div>
    `
        : nothing
    }

    <div style="margin-bottom:16px;">
      <strong>Channel Links:</strong>
      <div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:6px;">
        ${
          (user.channels ?? []).length > 0
            ? (user.channels ?? []).map(
                (ch) =>
                  html`<span class="pill pill--sm pill--ok">${ch.channel}: ${ch.channelUserId}</span>`,
              )
            : nothing
        }
        ${
          user.email
            ? html`<span class="pill pill--sm pill--ok">Email: ${user.email}</span>`
            : nothing
        }
        ${
          (user.channels ?? []).length === 0 && !user.email
            ? html`
                <span class="muted">No channels linked</span>
              `
            : nothing
        }
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <strong>Username:</strong>
      <span style="margin-left:6px;">${
        user.username
          ? html`@${user.username}`
          : html`
              <span class="muted">Not set</span>
            `
      }</span>
    </div>

    <div style="margin-bottom:16px;">
      <strong>Preferred Channel:</strong>
      <span class="muted" style="margin-left:6px;">${user.preferredChannel ?? "Not set"}</span>
    </div>

    <div style="margin-bottom:16px;">
      <button class="btn btn--sm" @click=${() => props.onResetPassword(user.id)}>
        Reset Password
      </button>
    </div>

    <!-- Email Credentials (per-user IMAP for draft creation) -->
    <div class="card" style="margin-bottom:16px;">
      <div class="card-title" style="font-size:0.9em;">Email Credentials</div>
      <div class="card-sub" style="font-size:0.75em;margin-bottom:8px;">
        IMAP credentials for creating draft emails in the user's mailbox. Bot can draft emails but not send them.
      </div>

      ${props.emailCredsError
        ? html`<div class="callout danger" style="margin-bottom:8px;">${props.emailCredsError}</div>`
        : nothing}
      ${props.emailCredsTestResult
        ? html`<div class="callout success" style="margin-bottom:8px;">${props.emailCredsTestResult}</div>`
        : nothing}

      ${props.emailCredsLoading
        ? html`<div class="muted" style="padding:8px;">Loading email credentials…</div>`
        : html`
          <div style="display:flex;gap:8px;margin-bottom:8px;align-items:center;">
            <label class="field" style="flex:0 0 auto;">
              <span>Type</span>
              <select
                .value=${props.emailCredsForm?.type ?? props.emailCreds?.type ?? "custom"}
                @change=${(e: Event) => props.onEmailCredsFormChange("type", (e.target as HTMLSelectElement).value)}
              >
                <option value="custom">Custom IMAP</option>
                <option value="gmail">Gmail (App Password)</option>
              </select>
            </label>
            <button class="btn btn--sm" @click=${props.onEmailCredsGmailPreset} title="Fill Gmail defaults">
              Gmail Preset
            </button>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            <label class="field">
              <span>IMAP Host</span>
              <input type="text"
                .value=${props.emailCredsForm?.imapHost ?? props.emailCreds?.imapHost ?? ""}
                @input=${(e: Event) => props.onEmailCredsFormChange("imapHost", (e.target as HTMLInputElement).value)}
                placeholder="imap.gmail.com"
              />
            </label>
            <label class="field">
              <span>IMAP Port</span>
              <input type="number"
                .value=${String(props.emailCredsForm?.imapPort ?? props.emailCreds?.imapPort ?? 993)}
                @input=${(e: Event) => props.onEmailCredsFormChange("imapPort", parseInt((e.target as HTMLInputElement).value, 10) || 993)}
              />
            </label>
            <label class="field">
              <span>SMTP Host</span>
              <input type="text"
                .value=${props.emailCredsForm?.smtpHost ?? props.emailCreds?.smtpHost ?? ""}
                @input=${(e: Event) => props.onEmailCredsFormChange("smtpHost", (e.target as HTMLInputElement).value)}
                placeholder="smtp.gmail.com"
              />
            </label>
            <label class="field">
              <span>SMTP Port</span>
              <input type="number"
                .value=${String(props.emailCredsForm?.smtpPort ?? props.emailCreds?.smtpPort ?? 587)}
                @input=${(e: Event) => props.onEmailCredsFormChange("smtpPort", parseInt((e.target as HTMLInputElement).value, 10) || 587)}
              />
            </label>
            <label class="field">
              <span>Username (email address)</span>
              <input type="text"
                .value=${props.emailCredsForm?.username ?? props.emailCreds?.username ?? ""}
                @input=${(e: Event) => props.onEmailCredsFormChange("username", (e.target as HTMLInputElement).value)}
                placeholder="user@gmail.com"
              />
            </label>
            <label class="field">
              <span>Password${props.emailCreds?.hasPassword ? " (leave blank to keep current)" : ""}</span>
              <input type="password"
                @input=${(e: Event) => props.onEmailCredsFormChange("password", (e.target as HTMLInputElement).value)}
                placeholder=${props.emailCreds?.hasPassword ? "•••••• (saved)" : "App Password or IMAP password"}
              />
            </label>
            <label class="field">
              <span>From Address</span>
              <input type="text"
                .value=${props.emailCredsForm?.fromAddress ?? props.emailCreds?.fromAddress ?? ""}
                @input=${(e: Event) => props.onEmailCredsFormChange("fromAddress", (e.target as HTMLInputElement).value)}
                placeholder="user@gmail.com"
              />
            </label>
            <label class="field">
              <span>From Name</span>
              <input type="text"
                .value=${props.emailCredsForm?.fromName ?? props.emailCreds?.fromName ?? ""}
                @input=${(e: Event) => props.onEmailCredsFormChange("fromName", (e.target as HTMLInputElement).value)}
                placeholder="John Doe"
              />
            </label>
          </div>

          <label style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:0.85em;cursor:pointer;">
            <input type="checkbox"
              .checked=${props.emailCredsForm?.tlsReject ?? props.emailCreds?.tlsReject ?? true}
              @change=${(e: Event) => props.onEmailCredsFormChange("tlsReject", (e.target as HTMLInputElement).checked)}
            />
            <span>Reject unauthorized TLS certificates</span>
          </label>

          <div style="display:flex;gap:8px;margin-top:12px;">
            <button class="btn btn--sm primary"
              ?disabled=${props.emailCredsSaving}
              @click=${() => props.onEmailCredsSave(user.id)}
            >${props.emailCredsSaving ? "Saving…" : "Save"}</button>
            <button class="btn btn--sm"
              ?disabled=${!props.emailCreds && !props.emailCredsForm}
              @click=${() => props.onEmailCredsTest(user.id)}
            >Test IMAP</button>
            ${props.emailCreds
              ? html`<button class="btn btn--sm" @click=${() => props.onEmailCredsDelete(user.id)}>Remove</button>`
              : nothing}
          </div>
        `}
    </div>

    <div style="margin-bottom:16px;">
      <strong>Project Assignments:</strong>
      ${
        props.selectedUserProjects.length === 0
          ? html`
              <div class="muted" style="margin-top: 6px">Not assigned to any projects.</div>
            `
          : html`
            <div style="margin-top:6px;">
              ${props.selectedUserProjects.map(
                (pa) => html`
                  <div style="display:flex;justify-content:space-between;align-items:center;padding:8px;border:1px solid var(--border);border-radius:4px;margin-bottom:4px;">
                    <div>
                      <strong>${pa.projectName}</strong>
                      <span class="pill pill--sm ${pa.role === "admin" || pa.role === "project_admin" ? "pill--ok" : ""}">
                        ${pa.role}
                      </span>
                    </div>
                    <div style="display:flex;gap:4px;align-items:center;">
                      <select
                        .value=${pa.role}
                        @change=${(e: Event) => props.onChangeRole(user.id, pa.projectId, (e.target as HTMLSelectElement).value as "admin" | "project_admin" | "user")}
                        style="padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;"
                      >
                        <option value="user" ?selected=${pa.role === "user"}>user</option>
                        <option value="project_admin" ?selected=${pa.role === "project_admin"}>project_admin</option>
                        <option value="admin" ?selected=${pa.role === "admin"}>admin</option>
                      </select>
                      <button
                        class="btn btn--sm"
                        @click=${() => props.onRemoveFromProject(user.id, pa.projectId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                `,
              )}
            </div>
          `
      }
    </div>

    ${
      props.projects.length > 0
        ? html`
      <div style="margin-bottom:16px;">
        <strong>Add to Project:</strong>
        <div style="display:flex;gap:6px;margin-top:6px;align-items:center;">
          <select id="add-to-project-select"
            style="flex:1;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
            ${props.projects.map((p) => html`<option value=${p.id}>${p.name}</option>`)}
          </select>
          <select id="add-to-project-role"
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
            <option value="user">user</option>
            <option value="project_admin">project_admin</option>
            <option value="admin">admin</option>
          </select>
          <button class="btn btn--sm primary" @click=${() => {
            const projectSelect = document.getElementById(
              "add-to-project-select",
            ) as HTMLSelectElement;
            const roleSelect = document.getElementById("add-to-project-role") as HTMLSelectElement;
            if (projectSelect?.value) {
              props.onAddToProject(
                user.id,
                projectSelect.value,
                roleSelect.value as "admin" | "project_admin" | "user",
              );
            }
          }}>Add</button>
        </div>
      </div>
    `
        : nothing
    }

    <div class="muted" style="font-size:0.8em;">
      Created: ${formatDate(user.createdAt)}
      ${user.lastSeenAt ? ` · Last seen: ${formatDate(user.lastSeenAt)}` : ""}
    </div>
  `;
}

function renderUserEditForm(props: UsersAdminProps) {
  const form = props.editForm!;
  const formRec = form as Record<string, unknown>;
  const fieldStyle =
    "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);margin-top:4px;";

  const channels = (formRec.channels ?? []) as UserChannel[];

  return html`
    <div class="card-title">Edit User</div>

    <div style="display:flex;flex-direction:column;gap:12px;margin-top:12px;">
      <label>
        <strong>Username</strong>
        <input type="text" .value=${formRec.username ?? ""}
          @input=${(e: Event) => props.onEditFormChange("username", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="johndoe" autocomplete="off" />
      </label>

      <div>
        <strong>Change Password</strong>
        <div class="muted" style="font-size:0.85em;margin-bottom:4px;">Leave blank to keep the current password.</div>
        <input type="password" .value=${formRec.password ?? ""}
          @input=${(e: Event) => props.onEditFormChange("password", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="New password (optional)" autocomplete="new-password" />
      </div>

      <label>
        <strong>First Name</strong>
        <input type="text" .value=${form.firstName ?? ""}
          @input=${(e: Event) => props.onEditFormChange("firstName", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="First name" />
      </label>

      <label>
        <strong>Last Name</strong>
        <input type="text" .value=${form.lastName ?? ""}
          @input=${(e: Event) => props.onEditFormChange("lastName", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="Last name" />
      </label>

      <label>
        <strong>Email</strong>
        <input type="email" .value=${form.email ?? ""}
          @input=${(e: Event) => props.onEditFormChange("email", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="user@example.com" />
      </label>

      <label>
        <strong>Position</strong>
        <input type="text" .value=${form.position ?? ""}
          @input=${(e: Event) => props.onEditFormChange("position", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="e.g. Sales Manager" />
      </label>

      <label>
        <strong>Profile Description</strong>
        <textarea .value=${form.profileDescription ?? ""}
          @input=${(e: Event) => props.onEditFormChange("profileDescription", (e.target as HTMLTextAreaElement).value)}
          style="${fieldStyle}min-height:60px;" placeholder="Brief description"></textarea>
      </label>

      <!-- Channels -->
      <div>
        <strong>Channels</strong>
        <div style="margin-top:6px;display:flex;flex-direction:column;gap:6px;">
          ${channels.map(
            (ch, i) => html`
            <div style="display:flex;gap:6px;align-items:center;">
              <select .value=${ch.channel}
                @change=${(e: Event) => {
                  const updated = [...channels];
                  updated[i] = { ...updated[i], channel: (e.target as HTMLSelectElement).value };
                  props.onEditFormChange("channels", updated);
                }}
                style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
                ${CHANNEL_OPTIONS.map((opt) => html`<option value=${opt} ?selected=${ch.channel === opt}>${opt}</option>`)}
              </select>
              <input type="text" .value=${ch.channelUserId}
                @input=${(e: Event) => {
                  const updated = [...channels];
                  updated[i] = {
                    ...updated[i],
                    channelUserId: (e.target as HTMLInputElement).value,
                  };
                  props.onEditFormChange("channels", updated);
                }}
                style="flex:1;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
                placeholder="Channel user ID" />
              <button class="btn btn--sm" style="color:var(--danger,#e55);"
                @click=${() => {
                  const updated = channels.filter((_, idx) => idx !== i);
                  props.onEditFormChange("channels", updated);
                }}>x</button>
            </div>
          `,
          )}
          <button class="btn btn--sm" @click=${() => {
            props.onEditFormChange("channels", [
              ...channels,
              { channel: "telegram", channelUserId: "" },
            ]);
          }}>+ Add Channel</button>
        </div>
      </div>

      <label>
        <strong>Preferred Channel</strong>
        <select .value=${form.preferredChannel ?? "telegram"}
          @change=${(e: Event) => props.onEditFormChange("preferredChannel", (e.target as HTMLSelectElement).value)}
          style=${fieldStyle}>
          ${CHANNEL_OPTIONS.map((opt) => html`<option value=${opt}>${opt}</option>`)}
        </select>
      </label>

      <label>
        <strong>Timezone</strong>
        <input type="text" .value=${form.timezone ?? "UTC"}
          @input=${(e: Event) => props.onEditFormChange("timezone", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="UTC, America/New_York, etc." />
      </label>

      <!-- Desktop App URLs (per-user remote desktop routing) -->
      <div style="border-top:1px solid var(--border);padding-top:12px;margin-top:4px;">
        <strong>Desktop App URLs</strong>
        <div class="muted" style="font-size:0.85em;margin-bottom:8px;">
          Per-user URLs for remote Thunderbird and LibreOffice instances (VPN-accessible).
          Leave blank to use global defaults.
        </div>
        <label>
          <strong style="font-weight:500;">Thunderbird URL</strong>
          <input type="url" .value=${(formRec.metadata as Record<string, unknown>)?.thunderbirdUrl ?? ""}
            @input=${(e: Event) => {
              const meta = (formRec.metadata as Record<string, unknown>) ?? {};
              props.onEditFormChange("metadata", {
                ...meta,
                thunderbirdUrl: (e.target as HTMLInputElement).value || undefined,
              });
            }}
            style=${fieldStyle} placeholder="http://192.168.1.50:8765" />
        </label>
        <label style="margin-top:8px;display:block;">
          <strong style="font-weight:500;">LibreOffice URL</strong>
          <input type="url" .value=${(formRec.metadata as Record<string, unknown>)?.libreofficeUrl ?? ""}
            @input=${(e: Event) => {
              const meta = (formRec.metadata as Record<string, unknown>) ?? {};
              props.onEditFormChange("metadata", {
                ...meta,
                libreofficeUrl: (e.target as HTMLInputElement).value || undefined,
              });
            }}
            style=${fieldStyle} placeholder="http://192.168.1.50:8200" />
        </label>
      </div>

      <div style="display:flex;gap:8px;margin-top:8px;">
        <button class="btn btn--primary" @click=${props.onSaveUser}>Save</button>
        <button class="btn" @click=${props.onCancelEdit}>Cancel</button>
      </div>
    </div>
  `;
}

function renderCreateUserForm(props: UsersAdminProps) {
  const form = props.createForm ?? {};
  const formRec = form as Record<string, unknown>;
  const fieldStyle =
    "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);margin-top:4px;";

  const channels = (formRec.channels ?? []) as UserChannel[];

  return html`
    <div class="card-title">Create New User</div>

    <div style="display:flex;flex-direction:column;gap:12px;margin-top:12px;">
      <label>
        <strong>Username <span style="color:var(--danger,#e55);">*</span></strong>
        <input type="text" .value=${formRec.username ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("username", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="johndoe" autocomplete="off" />
      </label>

      <label>
        <strong>Password <span style="color:var(--danger,#e55);">*</span></strong>
        <input type="password" .value=${formRec.password ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("password", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="Enter password" autocomplete="new-password" />
      </label>

      <label>
        <strong>First Name</strong>
        <input type="text" .value=${form.firstName ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("firstName", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="First name" />
      </label>

      <label>
        <strong>Last Name</strong>
        <input type="text" .value=${form.lastName ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("lastName", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="Last name" />
      </label>

      <label>
        <strong>Email</strong>
        <input type="email" .value=${form.email ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("email", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="user@example.com" />
      </label>

      <label>
        <strong>Position</strong>
        <input type="text" .value=${form.position ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("position", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="e.g. Sales Manager" />
      </label>

      <label>
        <strong>Profile Description</strong>
        <textarea .value=${form.profileDescription ?? ""}
          @input=${(e: Event) => props.onCreateFormChange("profileDescription", (e.target as HTMLTextAreaElement).value)}
          style="${fieldStyle}min-height:60px;" placeholder="Brief description"></textarea>
      </label>

      <!-- Channels -->
      <div>
        <strong>Channels</strong>
        <div style="margin-top:6px;display:flex;flex-direction:column;gap:6px;">
          ${channels.map(
            (ch, i) => html`
            <div style="display:flex;gap:6px;align-items:center;">
              <select .value=${ch.channel}
                @change=${(e: Event) => {
                  const updated = [...channels];
                  updated[i] = { ...updated[i], channel: (e.target as HTMLSelectElement).value };
                  props.onCreateFormChange("channels", updated);
                }}
                style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
                ${CHANNEL_OPTIONS.map((opt) => html`<option value=${opt} ?selected=${ch.channel === opt}>${opt}</option>`)}
              </select>
              <input type="text" .value=${ch.channelUserId}
                @input=${(e: Event) => {
                  const updated = [...channels];
                  updated[i] = {
                    ...updated[i],
                    channelUserId: (e.target as HTMLInputElement).value,
                  };
                  props.onCreateFormChange("channels", updated);
                }}
                style="flex:1;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
                placeholder="Channel user ID" />
              <button class="btn btn--sm" style="color:var(--danger,#e55);"
                @click=${() => {
                  const updated = channels.filter((_, idx) => idx !== i);
                  props.onCreateFormChange("channels", updated);
                }}>x</button>
            </div>
          `,
          )}
          <button class="btn btn--sm" @click=${() => {
            props.onCreateFormChange("channels", [
              ...channels,
              { channel: "telegram", channelUserId: "" },
            ]);
          }}>+ Add Channel</button>
        </div>
      </div>

      <label>
        <strong>Preferred Channel</strong>
        <select .value=${form.preferredChannel ?? "webchat"}
          @change=${(e: Event) => props.onCreateFormChange("preferredChannel", (e.target as HTMLSelectElement).value)}
          style=${fieldStyle}>
          ${CHANNEL_OPTIONS.map((opt) => html`<option value=${opt}>${opt}</option>`)}
        </select>
      </label>

      <label>
        <strong>Timezone</strong>
        <input type="text" .value=${form.timezone ?? "UTC"}
          @input=${(e: Event) => props.onCreateFormChange("timezone", (e.target as HTMLInputElement).value)}
          style=${fieldStyle} placeholder="UTC, America/New_York, etc." />
      </label>

      <div style="display:flex;gap:8px;margin-top:8px;">
        <button class="btn btn--primary" @click=${props.onSaveNewUser}>Create</button>
        <button class="btn" @click=${props.onCancelCreate}>Cancel</button>
      </div>
    </div>
  `;
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) {
    return "\u2014";
  }
  try {
    const str = typeof value === "string" ? value : value.toISOString();
    return str.split("T")[0] ?? "\u2014";
  } catch {
    return String(value);
  }
}

function renderChannelBadges(user: UserEntry) {
  const badges: string[] = [];
  for (const ch of user.channels ?? []) {
    const label = ch.channel.toUpperCase().slice(0, 2);
    badges.push(label);
  }
  if (user.email) {
    badges.push("Email");
  }
  if (user.username) {
    badges.push(`@${user.username}`);
  }
  return badges.length > 0 ? badges.join(" / ") : "No channels";
}
